import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import IcomoonReact from 'icomoon-react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Tooltip as ReactTooltip } from 'react-tooltip';

import iconSet from '../../../shared/images/teambuildr-selection.json';
import Text from '../../../shared/components/Text/Text';
import { loadSavedProgram } from '../ducks/calendarActions';

const CloseButton = styled('button')`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9E9E9E;
  transition: color 0.2s ease;

  &:hover {
    color: #666;
  }
`;

const ModalContent = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 16px;
  gap: 16px;
  position: relative;
  font-family: 'Nunito Sans';
`;

const Title = styled('h1')`
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  max-width: calc(100% - 40px); /* Account for close button */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SubText = styled(Text)`
  color: #666;
  margin-top: -16px;
`;

const DateContainer = styled('div')`
  display: flex;
  gap: 16px;
`;

const DatePickerWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  position: relative;

  .react-datepicker {
    border: none;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
    font-family: 'Nunito Sans';
    width: 320px;
  }

  .react-datepicker__header {
    background: white;
    border-bottom: none;
    padding: 15px;
  }

  .react-datepicker__month-container {
    padding: 15px;
    font-family: 'Nunito Sans';
    font-size: 15px;
  }

  .react-datepicker__day-names,
  .react-datepicker__week {
    margin-bottom: 15px;
  }

  .react-datepicker__day--outside-month {
    color: #BDBDBD;
  }

  .react-datepicker__day--inside-month {
    color: #444444;
  }

  .react-datepicker__day--selected {
    background-color: #FF6600;
    color: white;
  }

  .react-datepicker__day--keyboard-selected {
    background: none;
    color: #444444;
  }

  .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle,
  .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle::before {
    border-bottom-color: white;
  }

  .react-datepicker-popper {
    z-index: 1;
  }
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
  height: 40px;
  border: 1px solid #E0E0E0;
  border-radius: 4px;
  padding: 0 12px;
  font-size: 14px;
  color: #333;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  background: white;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${(props) => (props.disabled ? '#E0E0E0' : '#BDBDBD')};
  }

  &:focus {
    outline: none;
    border-color: #BDBDBD;
  }
`;

const StatsText = styled(Text)`
  text-align: right;
  color: #666;
  font-size: 14px;
`;

const DescriptionDisplay = styled('div')`
  width: 100%;
  padding: 12px;
  border-radius: 4px;
  font-size: 16px;
  background-color: white;
  color: #666;
  line-height: 1.4;
  overflow-y: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const CheckboxContainer = styled('div')`
  display: flex;
  gap: 16px;
  flex-direction: column;
`;

const CheckboxWrapper = styled('label')`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const Checkbox = styled('input')`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const ButtonContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;

const Button = styled('button')`
  flex: 1;
  padding: 8px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${(props) => (props.primary ? `
    background: #333;
    color: white;
    border: none;
    &:hover {
      background: #444;
    }
  ` : `
    background: white;
    color: #333;
    border: 2px solid #333;
    &:hover {
      background: #F5F5F5;
    }
  `)}
`;

const EndDateTitleWrapper = styled('div')`
  position: relative;
  display: inline-block;
  cursor: help;
`;

const LoadProgramsModal = ({ closeModal }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedSavedWorkout = useSelector((state) => state.calendar.data.selectedSavedWorkout);
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);

  // Helper functions to handle both data structures
  const getWorkoutId = () => selectedSavedWorkout?.info?.id || selectedSavedWorkout?.id;
  const getWorkoutName = () => selectedSavedWorkout?.info?.programName
  || selectedSavedWorkout?.programName;
  const getWorkoutDescription = () => selectedSavedWorkout?.info?.description
  || selectedSavedWorkout?.description;
  const getNumberOfDays = () => selectedSavedWorkout?.info?.numberOfDays
  || selectedSavedWorkout?.numberOfDays;
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);

  const [startDate, setStartDate] = useState(() => {
    // Handle case where selectedDate might be undefined/null
    if (!selectedDate) {
      return new Date(); // Default to today if no date selected
    }
    return new Date(selectedDate);
  });
  const [endDate, setEndDate] = useState(() => {
    const initialStartDate = selectedDate ? new Date(selectedDate) : new Date();
    return moment(initialStartDate).add(getNumberOfDays() || 0, 'days').toDate();
  });
  const [includeTitles, setIncludeTitles] = useState(true);
  const [includeLoads, setIncludeLoads] = useState(true);
  const [replaceWorkouts, setReplaceWorkouts] = useState(false);

  // Generate unique ID for tooltip
  const endDateTooltipId = 'end-date-tooltip';

  // Update start date when selectedDate changes from Redux
  useEffect(() => {
    if (selectedDate) {
      setStartDate(new Date(selectedDate));
    }
  }, [selectedDate]);

  // Update end date whenever start date changes
  useEffect(() => {
    const newEndDate = moment(startDate).add(getNumberOfDays() || 0, 'days').toDate();
    setEndDate(newEndDate);
  }, [startDate, selectedSavedWorkout]);

  const handleLoadProgram = () => {
    if (programType === 'calendar') {
    // Check if required data is available
      if (!getWorkoutId()) {
        toast.error('No program selected. Please select a program to load.');
        return;
      }

      if (!selectedReduxCalendar?.id && !selectedReduxAthlete?.id) {
        toast.error('Please select a calendar or user to load the program to');
        return;
      }

      const submissionObject = {
        clearExisting: replaceWorkouts,
        includePrescribedLoads: includeLoads,
        includeWorkoutInfo: includeTitles,
        programId: getWorkoutId(),
        startDate: moment(startDate).format('YYYY-MM-DD'),
      };

      if (Object.keys(selectedReduxAthlete).length) {
        submissionObject.userId = selectedReduxAthlete.id;
      } else if (Object.keys(selectedReduxCalendar).length) {
        submissionObject.calendarId = selectedReduxCalendar.id;
      }

      dispatch(loadSavedProgram(currentUser.accountCode, submissionObject));
    } else {
      console.log('hit enpoint to load program for non date based workout');
    }
  };

  return (
    <ModalContent>
      <CloseButton onClick={closeModal}>
        <IcomoonReact
          iconSet={iconSet}
          size={14}
          icon='remove'
        />
      </CloseButton>
      <Title>
        {getWorkoutName()
          ? `Load ${getWorkoutName()}`
          : 'Load Program'}
      </Title>
      <SubText>
        The start and end date for this program have been selected based on the chosen start date
        and the number of total days in the program.
      </SubText>
      <DateContainer>
        <DatePickerWrapper>
          <Text fontWeight={600}>Start Date</Text>
          <StyledDatePicker
            selected={moment.isMoment(startDate) ? startDate.toDate() : startDate}
            onChange={setStartDate}
            placeholderText='Select Date'
            dateFormat='MMM dd, yyyy'
          />
        </DatePickerWrapper>

        <DatePickerWrapper>
          <EndDateTitleWrapper>
            <div
              style={{ display: 'inline-block' }}
              data-tooltip-id={endDateTooltipId}
            >
              <Text fontWeight={600}>End Date</Text>
            </div>
          </EndDateTitleWrapper>
          <StyledDatePicker
            selected={moment.isMoment(endDate) ? endDate.toDate() : endDate}
            placeholderText='Select Date'
            dateFormat='MMM dd, yyyy'
            disabled
          />
        </DatePickerWrapper>
      </DateContainer>
      <StatsText>
        Active Days:
        {' '}
        {moment(endDate).diff(moment(startDate), 'days') + 1}
      </StatsText>

      <div>
        <Text fontWeight={600} style={{ marginBottom: '8px' }}>Description</Text>
        <DescriptionDisplay>
          {getWorkoutDescription() || ''}
        </DescriptionDisplay>
      </div>

      <CheckboxContainer>
        <CheckboxWrapper>
          <Checkbox
            type='checkbox'
            checked={includeTitles}
            onChange={(e) => setIncludeTitles(e.target.checked)}
          />
          <Text>Include workout titles and colors</Text>
        </CheckboxWrapper>
        <CheckboxWrapper>
          <Checkbox
            type='checkbox'
            checked={includeLoads}
            onChange={(e) => setIncludeLoads(e.target.checked)}
          />
          <Text>Include prescribed loads</Text>
        </CheckboxWrapper>
      </CheckboxContainer>

      <CheckboxWrapper>
        <Checkbox
          type='checkbox'
          checked={replaceWorkouts}
          onChange={(e) => setReplaceWorkouts(e.target.checked)}
        />
        <Text>Replace workouts for all selected dates</Text>
      </CheckboxWrapper>

      <ButtonContainer>
        <Button primary onClick={handleLoadProgram}>Load Program</Button>
        <Button onClick={closeModal}>CANCEL</Button>
      </ButtonContainer>

      {/* ReactTooltip component */}
      <ReactTooltip
        id={endDateTooltipId}
        place='top'
        content='End date is based on start date and active days'
        style={{
          backgroundColor: '#A6A6A6',
          color: 'white',
          fontFamily: 'Nunito Sans',
          fontSize: '13px',
        }}
      />
    </ModalContent>
  );
};

LoadProgramsModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
};

export default LoadProgramsModal;
